import { useState } from "react";
import { useFormBuilder } from "../hooks/useFormBuilder";
import { useNavigate, useSearchParams } from "react-router-dom";
import { TEMPLATE_LIST } from "../Components/FormTemplates";

function FormCreate() {
    const [searchParams] = useSearchParams();
    const [keyword, setKeyword] = useState(searchParams.get("prompt") || "");
    const [showDesign, setShowDesign] = useState(false);
    const [designPreferences, setDesignPreferences] = useState({
        style: "",
        colors: "",
        industry: "",
    });
    const { generateForm, loading, error } = useFormBuilder();
    const navigate = useNavigate();

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!keyword.trim()) return;
        const prefs =
            designPreferences.style ||
            designPreferences.colors ||
            designPreferences.industry
                ? designPreferences
                : undefined;
        const form = await generateForm(keyword, prefs);
        if (form) {
            navigate(`/form-builder/edit/${form.formId}`);
        }
    };

    return (
        <div className="min-h-screen max-w-4xl mx-auto px-4 sm:px-6 py-8">
            <div className="grid border border-gray-200 dark:border-gray-700/60 rounded-2xl shadow-sm p-6 font-Rajdhani bg-white dark:bg-[#23263a]">
                <h1 className="text-3xl font-bold mb-1">Create a Form with AI</h1>
                <p className="text-gray-500 dark:text-gray-400 font-medium mb-5">
                    Describe the form you want and AI will generate it instantly.
                </p>

                <form onSubmit={handleSubmit}>
                    <label className="text-lg font-semibold">
                        What kind of form do you need?
                    </label>
                    <textarea
                        className="mt-2 w-full rounded-lg p-3 border dark:border-gray-600 shadow-sm dark:bg-[#2c2f44] dark:text-[#99a0ac] font-normal focus:ring-2 focus:ring-indigo-300 focus:border-indigo-400 outline-none transition-all"
                        placeholder="e.g. Customer feedback survey, Job application form, Event registration..."
                        required
                        rows={3}
                        maxLength={500}
                        value={keyword}
                        onChange={(e) => setKeyword(e.target.value)}
                    />
                    <p className="text-sm text-gray-600 dark:text-gray-400 font-normal mt-1 text-right">
                        {keyword.length}/500
                    </p>

                    {/* Design Preferences */}
                    <button
                        type="button"
                        className="text-sm text-indigo-600 dark:text-indigo-400 font-medium mt-2 mr-10 hover:underline"
                        onClick={() => setShowDesign(!showDesign)}
                    >
                        {showDesign ? "- Hide" : "+ Show"} Design Preferences
                    </button>

                    {showDesign && (
                        <div className="mt-3 p-4 border dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-[#1e2035] space-y-3">
                            <div>
                                <label className="text-sm font-semibold">Style</label>
                                <select
                                    className="w-full border dark:border-gray-600 rounded p-2 mt-1 text-sm dark:bg-[#2c2f44] dark:text-[#99a0ac] font-normal"
                                    value={designPreferences.style}
                                    onChange={(e) =>
                                        setDesignPreferences({
                                            ...designPreferences,
                                            style: e.target.value,
                                        })
                                    }
                                >
                                    <option value="">Auto (let AI decide)</option>
                                    <option value="Clean & Minimal">Clean & Minimal</option>
                                    <option value="Bold & Modern">Bold & Modern</option>
                                    <option value="Playful & Colorful">Playful & Colorful</option>
                                </select>
                            </div>
                            <div>
                                <label className="text-sm font-semibold">Color Preference</label>
                                <input
                                    type="text"
                                    className="w-full border dark:border-gray-600 rounded p-2 mt-1 text-sm dark:bg-[#2c2f44] dark:text-[#99a0ac] font-normal"
                                    placeholder='e.g. "blue and white", "dark theme"'
                                    value={designPreferences.colors}
                                    onChange={(e) =>
                                        setDesignPreferences({
                                            ...designPreferences,
                                            colors: e.target.value,
                                        })
                                    }
                                />
                            </div>
                            <div>
                                <label className="text-sm font-semibold">Industry</label>
                                <input
                                    type="text"
                                    className="w-full border dark:border-gray-600 rounded p-2 mt-1 text-sm dark:bg-[#2c2f44] dark:text-[#99a0ac] font-normal"
                                    placeholder='e.g. "healthcare", "restaurant", "tech"'
                                    value={designPreferences.industry}
                                    onChange={(e) =>
                                        setDesignPreferences({
                                            ...designPreferences,
                                            industry: e.target.value,
                                        })
                                    }
                                />
                            </div>
                        </div>
                    )}

                    <button
                        className="mt-4 px-6 py-2.5 rounded-lg text-white bg-gradient-to-r from-indigo-600 to-indigo-500 dark:bg-[#818998] hover:opacity-90 disabled:opacity-50 font-semibold transition-opacity"
                        disabled={loading}
                        type="submit"
                    >
                        {loading ? (
                            <span className="flex items-center gap-2">
                                <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                                </svg>
                                Generating...
                            </span>
                        ) : (
                            "Generate with AI"
                        )}
                    </button>
                </form>

                {error && (
                    <p className="mt-3 text-red-600 dark:text-red-400 font-medium">
                        {error}
                    </p>
                )}
            </div>

            {/* Template Quick Start */}
            <div className="mt-8 font-Rajdhani">
                <h3 className="text-lg font-semibold mb-3">Quick Start with a Template</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {TEMPLATE_LIST.map((template) => (
                        <button
                            key={template.id}
                            onClick={() => {
                                setDesignPreferences({
                                    style:
                                        template.id === "dark"
                                            ? "Bold & Modern"
                                            : template.id === "colorful"
                                            ? "Playful & Colorful"
                                            : "Clean & Minimal",
                                    colors: "",
                                    industry: "",
                                });
                                setShowDesign(true);
                            }}
                            className="border dark:border-gray-700 rounded-lg p-4 text-left hover:shadow-md transition-shadow dark:bg-[#23263a]"
                        >
                            <div
                                className="h-3 rounded-full mb-3 w-16"
                                style={{ backgroundColor: template.primaryColor }}
                            />
                            <h4 className="font-semibold text-base">{template.name}</h4>
                            <p className="text-sm text-gray-500 font-normal mt-1">
                                {template.description}
                            </p>
                            <div className="flex gap-2 mt-2">
                                <span
                                    className="w-5 h-5 rounded border"
                                    style={{ backgroundColor: template.primaryColor }}
                                />
                                <span
                                    className="w-5 h-5 rounded border"
                                    style={{ backgroundColor: template.backgroundColor }}
                                />
                            </div>
                        </button>
                    ))}
                </div>
            </div>

            {/* How it works */}
            <div className="mt-8 mb-10 font-Rajdhani">
                <h3 className="text-lg font-semibold text-gray-700 dark:text-gray-300 mb-4">
                    How it works
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    {[
                        { step: 1, title: "Describe", desc: "Tell AI what form you need" },
                        { step: 2, title: "Generate", desc: "AI creates fields and design" },
                        { step: 3, title: "Customize", desc: "Edit fields, theme & settings" },
                        { step: 4, title: "Publish", desc: "Share your form link" },
                    ].map((item) => (
                        <div key={item.step} className="text-center">
                            <div className="w-10 h-10 rounded-full bg-indigo-100 dark:bg-indigo-900 text-indigo-600 dark:text-indigo-400 flex items-center justify-center mx-auto font-bold text-lg">
                                {item.step}
                            </div>
                            <h4 className="font-semibold mt-2">{item.title}</h4>
                            <p className="text-sm text-gray-500 font-normal">{item.desc}</p>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}

export default FormCreate;
